import { z } from 'zod';
import { AgentSlugSchema, CognitoSubSchema, TimestampSchema } from '../common.js';
// =========================================================
// ISO 639-1 language code
// =========================================================
/** Two-letter ISO 639-1 language code (e.g. "en", "fr"). */
export const Iso639_1Schema = z.string().regex(/^[a-z]{2}$/, {
    message: 'Must be an ISO 639-1 two-letter language code.',
});
// =========================================================
// CTA (call-to-action)
// =========================================================
export const PresetAnchorSchema = z.enum([
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
    'top-center',
    'bottom-center',
    'center',
]);
/** HTTP(S) destinations allowed for ordinary link CTAs. */
export const HttpUrlSchema = z
    .string()
    .url()
    .refine((u) => {
    try {
        const parsed = new URL(u);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
}, { message: 'URL must use http: or https:' });
/**
 * Convert an HTTP(S) URL or a commonly formatted phone number into the safe
 * destination stored for a CTA. Phone numbers are persisted as `tel:` URIs so
 * every consumer can launch the platform dialler without needing a separate
 * CTA type. Other URI schemes remain rejected.
 */
export function normalizeCtaDestination(value) {
    const trimmed = value.trim();
    if (HttpUrlSchema.safeParse(trimmed).success)
        return trimmed;
    const phone = trimmed.toLowerCase().startsWith('tel:') ? trimmed.slice(4).trim() : trimmed;
    const extensionMatch = phone.match(/(?:;ext=|\s*(?:ext(?:ension)?\.?|poste|x)\s*:?\s*#?\s*)(\d{1,10})$/i);
    const extension = extensionMatch?.[1];
    const subscriberNumber = extensionMatch?.index !== undefined ? phone.slice(0, extensionMatch.index).trim() : phone;
    const normalizedPhone = subscriberNumber.replace(/[\s().-]/g, '');
    if (!/^\+?\d{3,15}$/.test(normalizedPhone))
        return null;
    return `tel:${normalizedPhone}${extension ? `;ext=${extension}` : ''}`;
}
/**
 * CTA destination: an HTTP(S) URL or phone number. The transform means API
 * callers may submit a human-formatted number while stored entities always
 * contain a canonical `tel:` URI. Restricting the accepted schemes prevents
 * `javascript:`, `data:`, and similar values reaching the runtime link.
 */
export const CtaDestinationSchema = z.string().transform((value, ctx) => {
    const normalized = normalizeCtaDestination(value);
    if (normalized !== null)
        return normalized;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must be a valid http(s) URL or phone number.',
    });
    return z.NEVER;
});
export const CtaContentSchema = z.object({
    label: z.string().min(1).max(80),
    url: CtaDestinationSchema,
});
export const CtaSchema = z.object({
    id: z.string().uuid(),
    /**
     * Legacy/default-language fields retained for backward compatibility and
     * list views. New runtime surfaces read `content[lang]` first.
     */
    label: z.string().min(1).max(80),
    url: CtaDestinationSchema,
    content: z.record(Iso639_1Schema, CtaContentSchema).default({}),
    trigger: z.object({
        type: z.literal('time'),
        seconds: z.number().int().nonnegative(),
    }),
    position: PresetAnchorSchema,
    style: z.object({
        bg: z.string(),
        fg: z.string(),
    }),
});
// =========================================================
// Voice provider configs
// =========================================================
/** Current production defaults exposed by the agent-management UIs. */
export const OPENAI_REALTIME_DEFAULT_MODEL = 'gpt-realtime-2.1';
export const OPENAI_REALTIME_MINI_MODEL = 'gpt-realtime-2.1-mini';
export const OPENAI_REALTIME_MODEL_OPTIONS = [
    OPENAI_REALTIME_DEFAULT_MODEL,
    OPENAI_REALTIME_MINI_MODEL,
];
const LEGACY_OPENAI_REALTIME_MODELS = {
    'gpt-4o-realtime-preview': OPENAI_REALTIME_DEFAULT_MODEL,
    'gpt-4o-mini-realtime-preview': OPENAI_REALTIME_MINI_MODEL,
};
/**
 * Keep persisted preview-era agents operational without rewriting their rows.
 * Unknown model IDs are preserved so explicitly configured snapshots continue
 * to work and future model additions do not require a schema release.
 */
export function resolveOpenAIRealtimeModel(model) {
    return LEGACY_OPENAI_REALTIME_MODELS[model] ?? model;
}
export const OpenAIRealtimeConfigSchema = z.object({
    voiceProvider: z.literal('openai-realtime'),
    model: z.string(),
    /** Built-in OpenAI voice retained as the safe fallback for every agent. */
    voice: z.string(),
    /**
     * Opt-in pointer to a successfully activated custom voice profile. Existing
     * rows omit this field and continue using `voice` unchanged.
     */
    customVoiceProfileId: z.literal('primary').optional(),
    temperature: z.number().min(0).max(2).optional(),
    modalities: z.array(z.enum(['text', 'audio'])).optional(),
});
export const VapiConfigSchema = z.object({
    voiceProvider: z.literal('vapi'),
    assistantId: z.string(),
    overrides: z.record(z.unknown()).optional(),
});
// =========================================================
// Interaction modes
// =========================================================
export const AgentInteractionModeSchema = z.enum(['voice', 'text']);
/** Tenant preference that can only shorten the backoffice response policy. */
export const AgentResponseLengthSchema = z.enum(['default', 'short', 'very-short']);
// =========================================================
// Backoffice-owned runtime and commercial policy
// =========================================================
export const AgentResponseStyleSchema = z.enum(['concise', 'balanced', 'detailed']);
export const AgentTurnTakingProfileSchema = z.enum(['patient', 'balanced', 'responsive']);
export const AgentInterruptionModeSchema = z.enum(['allow', 'finish-response']);
export const AgentLanguageSwitchPolicySchema = z.enum(['locked', 'explicit-only', 'automatic']);
export const AgentOffTopicPolicySchema = z.enum(['redirect', 'decline', 'answer']);
export const AgentFactPolicySchema = z.enum(['approved-only', 'never']);
export const AgentCompetitorPolicySchema = z.enum(['decline', 'neutral', 'approved-only']);
export const AgentTechnicalAdvicePolicySchema = z.enum(['general-guidance', 'escalate']);
export const AgentPiiCollectionPolicySchema = z.enum(['none', 'contact-only']);
export const DEFAULT_AGENT_BEHAVIOR_POLICY = {
    responseStyle: 'balanced',
    maxSpokenSentences: 4,
    maxOutputTokens: 512,
    responseTimeoutMs: 20_000,
    turnTakingProfile: 'patient',
    interruptionMode: 'allow',
    protectOpeningGreeting: false,
    languageSwitchPolicy: 'explicit-only',
    offTopicPolicy: 'redirect',
    pricingPolicy: 'approved-only',
    availabilityPolicy: 'approved-only',
    competitorPolicy: 'approved-only',
    technicalAdvicePolicy: 'general-guidance',
    piiCollectionPolicy: 'none',
};
/**
 * Safe per-Agent policy choices. Platform security, tenant isolation, transcript
 * validation, response ownership, stale-tool suppression, and absolute limits
 * remain code-owned invariants and cannot be disabled through this object.
 */
export const AgentBehaviorPolicySchema = z
    .object({
    responseStyle: AgentResponseStyleSchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.responseStyle),
    maxSpokenSentences: z
        .number()
        .int()
        .min(1)
        .max(8)
        .default(DEFAULT_AGENT_BEHAVIOR_POLICY.maxSpokenSentences),
    maxOutputTokens: z
        .number()
        .int()
        .min(64)
        .max(1024)
        .default(DEFAULT_AGENT_BEHAVIOR_POLICY.maxOutputTokens),
    responseTimeoutMs: z
        .number()
        .int()
        .min(5_000)
        .max(45_000)
        .default(DEFAULT_AGENT_BEHAVIOR_POLICY.responseTimeoutMs),
    turnTakingProfile: AgentTurnTakingProfileSchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.turnTakingProfile),
    interruptionMode: AgentInterruptionModeSchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.interruptionMode),
    protectOpeningGreeting: z
        .boolean()
        .default(DEFAULT_AGENT_BEHAVIOR_POLICY.protectOpeningGreeting),
    languageSwitchPolicy: AgentLanguageSwitchPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.languageSwitchPolicy),
    offTopicPolicy: AgentOffTopicPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.offTopicPolicy),
    pricingPolicy: AgentFactPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.pricingPolicy),
    availabilityPolicy: AgentFactPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.availabilityPolicy),
    competitorPolicy: AgentCompetitorPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.competitorPolicy),
    technicalAdvicePolicy: AgentTechnicalAdvicePolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.technicalAdvicePolicy),
    piiCollectionPolicy: AgentPiiCollectionPolicySchema.default(DEFAULT_AGENT_BEHAVIOR_POLICY.piiCollectionPolicy),
})
    .default(DEFAULT_AGENT_BEHAVIOR_POLICY);
// =========================================================
// Localized public content
// =========================================================
export const AgentLocalizedContentSchema = z.object({
    /** Public display name for this language. Falls back to Agent.name for legacy default-language rows. */
    displayName: z.string().min(1).max(120).optional(),
    /** Language-specific main video mediaId. Falls back to Agent.videoMediaId for legacy default-language rows. */
    videoMediaId: z.string().uuid().nullable().optional(),
    /** Optional HEVC-with-alpha companion used by Safari on Apple mobile devices. */
    hevcVideoMediaId: z.string().uuid().nullable().optional(),
    /** Language-specific poster mediaId. Falls back to Agent.posterMediaId for legacy default-language rows. */
    posterMediaId: z.string().uuid().nullable().optional(),
});
// =========================================================
// Agent entity — see docs/data-model.md §4.6
// PK = TENANT#<tenantId>, SK = AGENT#<agentId>
// =========================================================
export const AgentSchema = z.object({
    agentId: z.string().uuid(),
    tenantId: z.string().uuid(),
    slug: AgentSlugSchema,
    name: z.string().min(1).max(120),
    /**
     * Backoffice-owned, non-dashboard-editable instructions that are composed
     * ahead of the tenant-managed per-language instruction at runtime.
     */
    guardrailPrompt: z.string().max(12_000).optional(),
    /** Structured backoffice-owned behavior and commercial constraints. */
    behaviorPolicy: AgentBehaviorPolicySchema,
    /** Legacy/default agents retain the full backoffice response policy. */
    responseLength: AgentResponseLengthSchema.default('default'),
    status: z.enum(['active', 'paused', 'archived']),
    /** Primary public interaction method. Existing rows default to voice. */
    interactionMode: AgentInteractionModeSchema.default('voice'),
    /** When voice is primary, expose a runtime affordance to switch to text chat in the same session. */
    textChatFallbackEnabled: z.boolean().default(false),
    /**
     * Saves the test-only async lip-animation selection. This flag defaults off
     * and is never sufficient to dispatch work without the restored control
     * plane, separately deployed worker, global API gate, exact pilot identity,
     * and bounded pilot window.
     */
    lipAnimationEnabled: z.boolean().default(false),
    voiceProvider: z.enum(['openai-realtime', 'vapi']),
    voiceProviderConfig: z.discriminatedUnion('voiceProvider', [
        OpenAIRealtimeConfigSchema,
        VapiConfigSchema,
    ]),
    languages: z.array(Iso639_1Schema).min(1),
    /** Publicly selectable languages. Draft translations can exist in `languages` without being active. */
    activeLanguages: z.array(Iso639_1Schema).default([]),
    defaultLang: Iso639_1Schema,
    localizedContent: z.record(Iso639_1Schema, AgentLocalizedContentSchema).default({}),
    backgroundKind: z.enum(['color', 'image']),
    /** Hex colour (#rrggbb) when backgroundKind = 'color', or a mediaId UUID when backgroundKind = 'image'. */
    backgroundValue: z.string(),
    /** mediaId of the main video asset; null when not yet uploaded. */
    videoMediaId: z.string().uuid().nullable(),
    /** Optional HEVC-with-alpha companion for the default-language main video. */
    hevcVideoMediaId: z.string().uuid().nullable().optional(),
    /** mediaId of the poster image; null when not yet uploaded. */
    posterMediaId: z.string().uuid().nullable(),
    ctas: z.array(CtaSchema).max(5),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    createdBy: CognitoSubSchema,
});
//# sourceMappingURL=agent.js.map